import React, { useReducer } from "react";

import axios from "axios";

import Dialog from "@busbud/design-system-components/dist/Dialog/Dialog";
import DialogActions from "@busbud/design-system-components/dist/DialogActions";
import DialogContent from "@busbud/design-system-components/dist/DialogContent/DialogContent";
import DialogTitle from "@busbud/design-system-components/dist/DialogTitle/DialogTitle";
import Text from "@busbud/design-system-components/dist/Text/Text";
import { Alert, Button, Input } from "@busbud/horizon";

import { useAppContext } from "@app/helpers/hooks";
import type { NapiUser } from "@app/types/napi-types";

import type { AnyAction } from "@reduxjs/toolkit";
import type { AxiosResponse } from "axios";

import { BasicLayout } from "../../../../components/basic-layout";
import * as ApiError from "../../../../helpers/api-error";
import * as Validator from "../../../../helpers/input-validator";
import {
  clickedConfirmNewPassword,
  signedIn
} from "../../../../tracking/account-tracking";
import { failedRequest } from "../../../../tracking/error-tracking";
import { redirect } from "../../../../utils/window";
import { getErrorText } from "../../utils";

const EXPIRED_TOKEN_ERR_CODES = [
  "PasswordResetTokenInvalid",
  "UserVerificationTokenInvalid"
];

interface Field {
  value: string;
  error?: string;
}

interface State {
  password: Field;
  confirmed_password: Field;
  error?: string;
  is_submitted: boolean;
}

const initial_state: State = {
  password: { value: "" },
  confirmed_password: { value: "" },
  is_submitted: false
};

const reducer = (state: State, action: AnyAction): State => {
  switch (action.type) {
    case "change":
      return {
        ...state,
        [action.field]: {
          value: action.value
        }
      };
    case "submit_form":
      return {
        ...state,
        password: { value: state.password.value },
        confirmed_password: { value: state.confirmed_password.value },
        error: undefined,
        is_submitted: true
      };
    case "error":
      return {
        ...state,
        [action.field]: {
          ...(state[action.field as keyof typeof state] as any),
          error: action.error
        },
        is_submitted: false
      };
    case "form_error": {
      return {
        ...state,
        error: action.error,
        is_submitted: false
      };
    }
    default:
      return state;
  }
};

interface Props {
  token: string;
}

export const ResetPasswordApp: React.FC<Props> = ({ token }) => {
  const { translator, urlBuilder, tracker } = useAppContext();
  const [state, dispatch] = useReducer(reducer, initial_state);

  const hasError = () => {
    const password_error = Validator.signupPassword(state.password.value);
    if (password_error) {
      dispatch({
        type: "error",
        field: "password",
        error: getErrorText(
          translator,
          `password.signup.${password_error.name}`
        )
      });
      return true;
    }

    const confirmed_password_error = Validator.signupPassword(
      state.confirmed_password.value
    );
    if (confirmed_password_error) {
      dispatch({
        type: "error",
        field: "confirmed_password",
        error: getErrorText(
          translator,
          `password.signup.${confirmed_password_error.name}`
        )
      });
      return true;
    }

    if (state.password.value !== state.confirmed_password.value) {
      dispatch({
        type: "error",
        field: "confirmed_password",
        error: getErrorText(translator, "password.passwords_do_not_match")
      });
      return true;
    }
  };

  const onSuccess = (response: AxiosResponse<NapiUser>) => {
    tracker.track(clickedConfirmNewPassword());
    tracker.track(signedIn(response.data.id));
    redirect(urlBuilder.accountTrips());
  };

  const onError = (err: Error | null) => {
    const bb_err = ApiError.toUserVisibleError(err);
    if (!bb_err) {
      return dispatch({ type: "form_error" });
    }
    tracker.track(failedRequest(err, "confirm_new_password"));

    if (EXPIRED_TOKEN_ERR_CODES.includes(bb_err.name)) {
      redirect(urlBuilder.accountForgotPassword(true));
    } else {
      const error = getErrorText(translator, bb_err.name);
      dispatch({ type: "form_error", error });
    }
  };

  const onSubmit: React.FormEventHandler = event => {
    event.preventDefault();
    if (state.is_submitted) {
      return;
    }
    dispatch({ type: "submit_form" });

    if (hasError()) {
      return;
    }

    axios
      .post<NapiUser>("/auth/change-password", {
        token,
        password: state.password.value
      })
      .then(onSuccess)
      .catch(onError);
  };

  return (
    <BasicLayout>
      <Dialog open maxWidth="sm" fullWidth>
        <form autoComplete="off" onSubmit={onSubmit}>
          <DialogTitle>
            {translator.t("!account.reset-password.reset.title")}
          </DialogTitle>
          <DialogContent>
            <div className="flex flex-col gap-200">
              {!!state.error && (
                <Alert
                  severity="negative"
                  hasRoleAlert
                  className={!state.error ? "sr-only" : undefined}
                >
                  <Alert.Description>{state.error}</Alert.Description>
                </Alert>
              )}
              <Text size="sm" lead>
                {translator.t("!account.reset-password.reset.message")}
              </Text>
              <Input
                type="password"
                name="password"
                id="password"
                labelText={translator.t("!account.reset-password.password")}
                size="md"
                value={state.password.value}
                isInvalid={!!state.password.error}
                errorText={state.password.error}
                onChange={e =>
                  dispatch({
                    type: "change",
                    field: "password",
                    value: e.target.value
                  })
                }
                isRequired
                className="w-full"
              />
              <Input
                type="password"
                name="confirmed_password"
                id="confirmed_password"
                labelText={translator.t(
                  "!account.reset-password.confirm-password"
                )}
                size="md"
                value={state.confirmed_password.value}
                isInvalid={!!state.confirmed_password.error}
                errorText={state.confirmed_password.error}
                onChange={e =>
                  dispatch({
                    type: "change",
                    field: "confirmed_password",
                    value: e.target.value
                  })
                }
                isRequired
                className="w-full"
              />
            </div>
          </DialogContent>
          <DialogActions>
            <Button size="md" url={urlBuilder.accountSignin()}>
              {translator.t("!account.reset-password.reset.cancel")}
            </Button>
            <Button
              size="md"
              isSubmit
              appearance="primary"
              isDisabled={state.is_submitted}
            >
              {translator.t("!account.reset-password.submit")}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </BasicLayout>
  );
};
